import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { Asset } from '@/types/models/assets.types';
import { AssetSensorsHealthStatus } from '@/types/models/assets.types';
import { DiagnosticsRequestsQueryParams } from '@/types/api/diagnostics.types';
import { baseQueryWithStatusCheck } from '@/store/baseQueryWithStatusCheck';
import { SENSOR_TYPE } from '@/const';
import { Site } from '@/types/models/sites.types';
import { Fault } from '@/types/models/faults.types';

const baseQuery = retry(baseQueryWithStatusCheck, { maxRetries: 2 });

export const diagnosticApi = createApi({
  reducerPath: 'diagnosticApi',
  baseQuery,
  tagTypes: [
    'Sites',
    'Assets',
    'OrganizationSummary',
    'Diagnostics',
    'Sensors',
    'Feedback',
    'Resource',
    'SensorsPreview',
    'Faults',
  ],
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getSites: builder.query<Array<Site>, { refreshCache?: boolean }>({
      query: ({ refreshCache = false }) => ({
        url: `diagnostic/sites_preview`,
        params: refreshCache ? { refresh_cache: 'true' } : {},
      }),
      providesTags: (result = []) => [
        ...result.map(({ site_id }) => ({ type: 'Sites', id: site_id } as const)),
        { type: 'Sites' as const, id: 'LIST' },
      ],
    }),

    getAssets: builder.query<Array<Asset>, any>({
      query: ({ site_id }) => ({
        url: `diagnostic/assets_preview?site_id=${site_id}`,
      }),
      providesTags: (result = []) => [
        ...result.map(({ asset_id }) => ({ type: 'Assets', asset_id } as const)),
        { type: 'Assets' as const, id: 'LIST' },
      ],
      transformResponse: (response: any, meta: any) => {
        const updatedResponse = response.map((asset: any) => {
          return {
            ...asset,
            show_diagnostics_day: asset.asset_category === 'mobile' ? 21 : 183,
          };
        });
        return updatedResponse;
      },
    }),

    getResource: builder.query<
      { content: string | null; type: string; signed_url: string | null },
      { resource_id: string; signed_url?: boolean }
    >({
      query: ({ resource_id, signed_url }) => ({
        url: `diagnostic/resource?resource_id=${resource_id}${signed_url ? '&signed_url=true' : ''}`,
      }),
      extraOptions: { maxRetries: 1 }, // Signed URL responses are tiny (~200 bytes), safe to retry
      // Use resource-specific cache tags to prevent cache collisions
      providesTags: (result, error, { resource_id }) => [{ type: 'Resource', id: resource_id }],
      // Add size validation warning for large resources
      transformResponse: (response: { content: string | null; type: string; signed_url: string | null }) => {
        if (response.content) {
          const sizeInBytes = response.content.length * 0.75; // base64 to bytes
          const sizeInMB = sizeInBytes / (1024 * 1024);

          if (sizeInMB > 10) {
            console.warn(`Large resource (${sizeInMB.toFixed(2)}MB): ${response.type}`);
          }
        }

        return response;
      },
    }),

    getSensors: builder.query<AssetSensorsHealthStatus, void>({
      query: () => ({ url: 'sensors_health_status' }),
      providesTags: () => [{ type: 'Sensors' }],
    }),

    getDiagnostics: builder.query<any, DiagnosticsRequestsQueryParams>({
      query: ({ site_id, assets_ids, from_date, to_date, asset_category }) => {
        const searchParams = new URLSearchParams();

        if (site_id) {
          searchParams.append('site_id', site_id);
        }

        if (assets_ids) {
          // Use unified parameter name for both mobile and fixed assets
          searchParams.append('assets_ids', assets_ids);
        }

        if (from_date) {
          searchParams.append('from_date', from_date);
        }

        if (to_date) {
          searchParams.append('to_date', to_date);
        }

        // Use unified endpoint for both mobile and fixed assets
        return {
          url: `diagnostic/diagnostics?${searchParams.toString()}`,
        };
      },
      transformResponse: (response: any, meta: any) => {
        const mutatedResponse = Object.entries(response).map((el: any) => {
          return [
            el?.[0],
            {
              assets: el[1].assets.map((asset: any) => {
                return {
                  ...asset,
                  hasCamera: asset?.components?.some((component: any) =>
                    component?.diagnostics?.some((diagnostic: any) =>
                      diagnostic?.sources_info?.some(
                        (source: any) => source?.source_type?.toLowerCase() === SENSOR_TYPE.Camera
                      )
                    )
                  ),
                  hasOilReport: asset?.components?.some((component: any) =>
                    component?.diagnostics?.some((diagnostic: any) =>
                      diagnostic?.sources_info?.some(
                        (source: any) => source?.source_type?.toLowerCase() === SENSOR_TYPE.OilReport
                      )
                    )
                  ),
                  components: asset?.components?.map((component: any) => {
                    return {
                      ...component,
                      diagnostics: component?.diagnostics?.map((diag: any) => ({
                        ...diag,
                        component_id: component.component_id,
                      })),
                      hasCamera: component?.diagnostics?.some((diagnostic: any) =>
                        diagnostic?.sources_info?.some(
                          (source: any) => source.source_type.toLowerCase() === SENSOR_TYPE.Camera
                        )
                      ),
                      hasOilReport: component?.diagnostics?.some((diagnostic: any) =>
                        diagnostic?.sources_info?.some(
                          (source: any) => source.source_type.toLowerCase() === SENSOR_TYPE.OilReport
                        )
                      ),
                    };
                  }),
                };
              }),
              site_id: el[1]?.site_id,
            },
          ];
        });

        const transformedResponse = mutatedResponse.sort((a, b) => {
          const dateA = Date.parse(a[0]);
          const dateB = Date.parse(b[0]);

          return dateA - dateB;
        });

        // transformedResponse.forEach((entry: any) => {
        //   entry[1].assets[0].components.forEach((component: any) => {
        //     component.unit_ui_name = !component.unit_ui_name ? 'General Component' : !component.unit_ui_name;
        //   });
        // });

        return transformedResponse;
      },
      providesTags: [{ type: 'Diagnostics' }],
    }),

    getDiagnosticsByFormula: builder.query<any, any>({
      query: ({ site_id, asset_id, formula_id, from_date, to_date }) => {
        const searchParams = new URLSearchParams();

        if (site_id) {
          searchParams.append('site_id', site_id);
        }

        if (asset_id) {
          searchParams.append('asset_id', asset_id);
        }

        if (formula_id) {
          searchParams.append('formula_id', formula_id);
        }

        if (from_date) {
          searchParams.append('from_date', from_date);
        }

        if (to_date) {
          searchParams.append('to_date', to_date);
        }

        return {
          url: `diagnostic/mf_diagnostics_by_formula?${searchParams.toString()}`,
        };
      },
      providesTags: [{ type: 'Diagnostics' }],
    }),

    getSensorsPreview: builder.query<
      { [key: string]: AssetSensorsHealthStatus[] },
      {
        siteId: string;
        assetId?: string;
      }
    >({
      query: ({ siteId, assetId }) => {
        return { url: `diagnostic/sensors_metadata?site_id=${siteId}` + (assetId ? `&asset_ids=${assetId}` : '') };
      },
      providesTags: (result, error, arg) => [{ type: 'SensorsPreview', id: arg.assetId || 'LIST' }],
    }),

    getFaults: builder.query<Array<Fault>, any>({
      query: ({ site_id, from_date, to_date, assets_type, asset_id, component_id }) => {
        const searchParams = new URLSearchParams();

        if (site_id) {
          searchParams.append('site_id', site_id);
        }

        if (assets_type) {
          searchParams.append('type', assets_type);
        }

        //if from to date not sent - will receive faults list of last 2 weeks
        if (from_date) {
          searchParams.append('from_date', from_date);
        }

        if (to_date) {
          searchParams.append('to_date', to_date);
        }

        if (asset_id) {
          searchParams.append('asset_id', asset_id);
        }

        if (component_id) {
          searchParams.append('component_id', component_id);
        }

        return {
          url: `faults/v1?${searchParams.toString()}&view=active`,
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ fault_id }) => ({
                type: 'Faults' as const,
                id: fault_id,
              })),
              { type: 'Faults' as const, id: 'LIST' },
            ]
          : [{ type: 'Faults' as const, id: 'LIST' }];
      },

      transformResponse: (response: any, meta: any) => {
        //   //FIXME: add here all the other fields for now -  what is missing...
        const { open_faults, summary } = response;

        const updatedResponse = open_faults.map((open_fault: any) => {
          return {
            ...open_fault,
            //FIXME:remove
            asset_category: response.type === 'mf' ? 'mobile' : 'fixed',
          };
        });
        return updatedResponse;
      },
    }),

    updateFeedback: builder.mutation<any, any>({
      query({ diagnostic_id, feedback_type, feedback, description }) {
        const searchParams = new URLSearchParams();
        if (diagnostic_id) {
          searchParams.append('diagnostic_id', diagnostic_id);
        }
        if (feedback_type) {
          searchParams.append('feedback_type', feedback_type);
        }
        if (feedback) {
          searchParams.append('feedback', feedback);
        }
        if (description) {
          searchParams.append('description', description);
        }

        return {
          url: `diagnostic/update_feedback?${searchParams.toString()}`,
          method: 'POST',
        };
      },
    }),
  }),
});

export const {
  useGetSitesQuery,
  useGetAssetsQuery,
  useLazyGetSitesQuery,
  useLazyGetAssetsQuery,
  useLazyGetDiagnosticsQuery,
  useGetFaultsQuery,
  useLazyGetFaultsQuery,
  useUpdateFeedbackMutation,
  useLazyGetResourceQuery,
  useLazyGetDiagnosticsByFormulaQuery,
  useLazyGetSensorsPreviewQuery,
} = diagnosticApi;
